import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { SelectedPosition } from '../types';

export const createDuplicateName = (originalName: string, names: string[]) => {
  const regex = new RegExp(`^\\s*${originalName}\\s*\\((\\d+)\\)\\s*$`);
  // we start duplicates from 2
  let maxNumber = 1;
  names.forEach((str) => {
    const match = str.match(regex);
    if (match) {
      const number = parseInt(match[1], 10); // Extract the number
      if (maxNumber === null || number > maxNumber) {
        maxNumber = number;
      }
    }
  });

  return `${originalName} (${maxNumber + 1})`;
};

// This function prevents sending non-relevant ERP positions
// (like TITLE without articles, TITLE with articles with quantity equal 0
// or duplicated Titles or Title sums)

export const updateNonRelevantPositions = (
  selectedPositions: SelectedPosition[]
) => {
  // extract the major number of position (reserved for Title)
  const findMajorNumber = (number: string) => number?.split('.')?.[0] ?? null;

  // Select only the last Title in a block of consecutive Titles
  for (let i = 0; i < selectedPositions.length; i++) {
    const current = selectedPositions[i];
    const next = selectedPositions[i + 1];

    if (current.position.positionKind === ErpPositionsKind.TITEL) {
      const isNextAlsoTitel =
        next?.position.positionKind === ErpPositionsKind.TITEL;

      // If the next item is also a Title, this is not the last in the block — deselect it
      if (isNextAlsoTitel) {
        selectedPositions[i] = {
          ...current,
          selected: false,
        };
      }
      // Otherwise, this is the last in the block or a standalone Title — leave it selected
    }
  }

  let titelsummeSeen = false;

  for (let idx = 0; idx < selectedPositions.length; idx++) {
    const item = selectedPositions[idx];
    const { position } = item;

    // Ensure only the first Title sum with a given major number is processed
    if (position.positionKind === ErpPositionsKind.TITELSUMME) {
      if (titelsummeSeen) {
        selectedPositions[idx] = { ...item, selected: false };
        continue;
      } else {
        titelsummeSeen = true;
      }
    } else {
      titelsummeSeen = false;
    }

    // Skip non-title entries — we only act when we find a Title
    if (position.positionKind !== ErpPositionsKind.TITEL) continue;

    const titelMajorNumber = findMajorNumber(position.number);

    // Find the next Title entry (to limit the range of children)
    const nextTitelIndex = selectedPositions
      .slice(idx + 1)
      .findIndex(
        (child) => child.position.positionKind === ErpPositionsKind.TITEL
      );
    const nextTitelItem =
      nextTitelIndex !== -1
        ? selectedPositions[idx + 1 + nextTitelIndex]
        : null;

    const relatedItems = selectedPositions.slice(idx + 1).filter((child) => {
      const childNumber = findMajorNumber(child.position.number);

      if (child.position.positionKind === ErpPositionsKind.TITEL) return false;
      // Stop at the next Title index
      if (nextTitelItem && child.position.index >= nextTitelItem.position.index)
        return false;
      // Ensure child belongs to the same major number group
      if (childNumber && childNumber !== titelMajorNumber) return false;

      return true;
    });

    // Types of children that make a Title "relevant"
    const relevantChildren = relatedItems.filter(
      (child) =>
        [
          ErpPositionsKind.STANDARD,
          ErpPositionsKind.STUECKLISTE,
          ErpPositionsKind.INTERNE_STUECKLISTE,
        ].includes(child.position.positionKind) && !child.position.posId
    );

    const firstRelevantChild = relevantChildren[0];
    const firstRelevantChildIndex = firstRelevantChild
      ? selectedPositions.findIndex(
          (x) => x.position.id === firstRelevantChild.position.id
        )
      : -1;

    const noRelevantChildren = relevantChildren.length === 0;
    const allRelevantChildrenAreEmpty = relevantChildren.every(
      (child) => child.position.amount === 0
    );

    if (noRelevantChildren || allRelevantChildrenAreEmpty) {
      // Deselect the Title
      selectedPositions[idx] = { ...item, selected: false };

      // Deselect its children
      relatedItems.forEach((childItem) => {
        const childIdx = selectedPositions.findIndex(
          (x) => x.position.id === childItem.position.id
        );
        if (childIdx !== -1) {
          selectedPositions[childIdx] = { ...childItem, selected: false };
        }
      });

      // Deselect directly following Title sum if exist
      const nextItem = selectedPositions[idx + 1];
      if (nextItem?.position.positionKind === ErpPositionsKind.TITELSUMME) {
        selectedPositions[idx + 1] = { ...nextItem, selected: false };
      }
    } else {
      // If Title sum  exists *before* the first relevant child, deselect it as well
      if (firstRelevantChildIndex !== -1) {
        for (let i = idx + 1; i < firstRelevantChildIndex; i++) {
          const possibleSumme = selectedPositions[i];
          if (
            possibleSumme.position.positionKind === ErpPositionsKind.TITELSUMME
          ) {
            selectedPositions[i] = {
              ...possibleSumme,
              selected: false,
            };
          }
        }
      }
    }
  }

  return selectedPositions;
};
